'use client';

import { useState } from 'react';
import { monthlyPI, CA_INSURANCE_RATE } from '@/lib/mortgage';
import { savingsVsCommission, commissionAt } from '@/lib/savings';
import PaymentSplitBar from '@/components/marketing/PaymentSplitBar';

const fmt = (n: number) => `$${Math.round(n).toLocaleString()}`;

interface FieldProps {
  label: string;
  suffix?: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format?: (v: number) => string;
}

function SliderField({ label, suffix, value, min, max, step, onChange, format }: FieldProps) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label className="text-sm font-medium text-ink-soft">{label}</label>
        <span className="font-semibold tabular-nums text-ink">
          {format ? format(value) : value}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-label={label}
        className="h-2 w-full cursor-pointer appearance-none rounded-full bg-raised accent-emerald-500"
      />
    </div>
  );
}

export default function MortgageCalculator() {
  const [price, setPrice] = useState(1_200_000);
  const [downPct, setDownPct] = useState(20);
  const [rate, setRate] = useState(6.5);
  const [years, setYears] = useState(30);
  const [taxRate, setTaxRate] = useState(1.2);
  const [insRate, setInsRate] = useState(CA_INSURANCE_RATE * 100);
  const [hoa, setHoa] = useState(0);

  const downPayment = (price * downPct) / 100;
  const principal = price - downPayment;
  const pi = monthlyPI(principal, rate, years);
  const tax = (price * (taxRate / 100)) / 12;
  const insurance = (price * (insRate / 100)) / 12; // homeowner's insurance, %/yr
  const total = pi + tax + insurance + hoa;

  // Conservative 2.5% end of the typical 2.5–3% buyer's-agent commission, so the
  // figure is a floor — at 3% a buyer keeps even more.
  const traditionalCommission = commissionAt(price);
  const savings = savingsVsCommission(price);

  return (
    <div className="grid gap-8 lg:grid-cols-[1.1fr_1fr]">
      {/* Inputs */}
      <div className="glass space-y-6 rounded-card p-7">
        <SliderField
          label="Home price"
          value={price}
          min={300_000}
          max={4_000_000}
          step={25_000}
          onChange={setPrice}
          format={fmt}
        />
        <SliderField
          label="Down payment"
          suffix="%"
          value={downPct}
          min={3}
          max={50}
          step={1}
          onChange={setDownPct}
        />
        <SliderField
          label="Interest rate"
          suffix="%"
          value={rate}
          min={3}
          max={10}
          step={0.125}
          onChange={setRate}
        />
        <SliderField
          label="Loan term"
          suffix=" yrs"
          value={years}
          min={10}
          max={30}
          step={5}
          onChange={setYears}
        />
        <SliderField
          label="Property tax"
          suffix="%"
          value={taxRate}
          min={0.7}
          max={2}
          step={0.05}
          onChange={setTaxRate}
        />
        <SliderField
          label="Home insurance"
          suffix="%"
          value={insRate}
          min={0.1}
          max={1}
          step={0.05}
          onChange={setInsRate}
        />
        <SliderField
          label="HOA dues"
          value={hoa}
          min={0}
          max={1500}
          step={25}
          onChange={setHoa}
          format={(v) => `$${v.toLocaleString()}/mo`}
        />
      </div>

      {/* Results */}
      <div className="space-y-5">
        <div className="glass rounded-card p-7">
          <p className="text-sm text-ink-muted">Estimated monthly payment</p>
          <p className="font-display mt-1 text-5xl tabular-nums text-ink">
            {fmt(total)}
            <span className="font-sans text-lg text-ink-muted">/mo</span>
          </p>
          {/* The proportions, before the figures. "How much of this is tax" is
              the question a first-time California buyer is actually asking, and
              four right-aligned dollar amounts make them do the arithmetic for
              it. The list below still carries every exact number. */}
          <PaymentSplitBar
            parts={[
              { label: 'Principal & interest', amount: pi, className: 'bg-emerald-500' },
              { label: 'Property tax', amount: tax, className: 'bg-gold-500' },
              { label: 'Insurance', amount: insurance, className: 'bg-emerald-200' },
              { label: 'HOA', amount: hoa, className: 'bg-ink-faint' },
            ]}
          />

          <dl className="mt-5 space-y-2 border-t border-hairline pt-4 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-muted">Principal &amp; interest</dt>
              <dd className="tabular-nums text-ink-soft">{fmt(pi)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-muted">Property tax</dt>
              <dd className="tabular-nums text-ink-soft">{fmt(tax)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-muted">Insurance (est.)</dt>
              <dd className="tabular-nums text-ink-soft">{fmt(insurance)}</dd>
            </div>
            {hoa > 0 && (
              <div className="flex justify-between">
                <dt className="text-ink-muted">HOA</dt>
                <dd className="tabular-nums text-ink-soft">{fmt(hoa)}</dd>
              </div>
            )}
            <div className="flex justify-between border-t border-hairline pt-2">
              <dt className="text-ink-muted">Down payment ({downPct}%)</dt>
              <dd className="tabular-nums text-ink-soft">{fmt(downPayment)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-muted">Loan amount</dt>
              <dd className="tabular-nums text-ink-soft">{fmt(principal)}</dd>
            </div>
          </dl>
        </div>

        <div className="rounded-card border border-gold-500/25 bg-gold-500/8 p-7">
          <p className="text-sm text-ink-muted">Buying this home with GoReal keeps at least</p>
          <p className="font-display mt-1 text-4xl tabular-nums text-gold-400">{fmt(savings)}</p>
          <p className="mt-2 text-sm leading-relaxed text-ink-muted">
            in your pocket vs. a 2.5% buyer&apos;s-agent commission ({fmt(traditionalCommission)}) —
            our fee is a flat $5,000. At a 3% commission you keep even more.
          </p>
        </div>

        <p className="text-xs leading-relaxed text-ink-faint">
          Estimates only, for planning. Actual rates, taxes, insurance, and lender fees vary. Not
          financial advice — talk to a lender for a real quote.
        </p>
      </div>
    </div>
  );
}
